import React, { useState, useEffect } from 'react';
import { deviceService } from '../services/api';

// Helper function to sanitize and validate IMEI input
// IMEI is a 15-digit number used to identify mobile devices
const sanitizeImei = (value) => {
  return value.replace(/\D/g, '').slice(0, 15);
};

const isValidImei = (imei) => {
  return !imei || /^\d{15}$/.test(imei);
};

// Create Device Modal Component
export const CreateDeviceModal = ({ enums, onClose, onSuccess, onError }) => {
  const [formData, setFormData] = useState({
    nodeName: '',
    imei: '',
    serialNumber: '',
    widevineId: '',
    platformType: 'in_cab',
    geoMobility: 'mobile',
    city: 'mumbai',
    aspectRatio: '16:9',
    screenWidth: '',
    screenHeight: '',
    minBid: '0.45',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.nodeName.trim()) {
      onError('Device name is required');
      return;
    }
    if (!formData.imei && !formData.serialNumber && !formData.widevineId) {
      onError('At least one hardware identifier (IMEI, Serial Number, or Widevine ID) is required');
      return;
    }
    if (!isValidImei(formData.imei)) {
      onError('IMEI must be exactly 15 digits');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        nodeName: formData.nodeName.trim(),
        imei: formData.imei || undefined,
        serialNumber: formData.serialNumber || undefined,
        widevineId: formData.widevineId || undefined,
        platformType: formData.platformType,
        geoMobility: formData.geoMobility,
        city: formData.city,
        aspectRatio: formData.aspectRatio,
        screenWidth: formData.screenWidth ? parseFloat(formData.screenWidth) : 0,
        screenHeight: formData.screenHeight ? parseFloat(formData.screenHeight) : 0,
        minBid: formData.minBid ? parseFloat(formData.minBid) : 0.45,
      };
      const response = await deviceService.create(payload);
      onSuccess(response.message || 'Device added to inventory');
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to create device');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📱 Add Device to Inventory</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Device Name */}
            <div className="form-group">
              <label>Device Name *</label>
              <input
                type="text"
                value={formData.nodeName}
                onChange={e => setFormData({...formData, nodeName: e.target.value})}
                placeholder="e.g., Cab-MH-001"
                minLength={2}
                maxLength={100}
                required
              />
              <span className="form-help">2-100 characters. Friendly identifier for this device.</span>
            </div>

            {/* Hardware Identifiers Section */}
            <div className="form-section">
              <h3>Hardware Identifiers (at least one required)</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>IMEI Number</label>
                  <input
                    type="text"
                    value={formData.imei}
                    onChange={e => setFormData({...formData, imei: sanitizeImei(e.target.value)})}
                    placeholder="15 digits"
                    maxLength={15}
                  />
                  <span className="form-help">International Mobile Equipment Identity</span>
                </div>
                <div className="form-group">
                  <label>Serial Number</label>
                  <input
                    type="text"
                    value={formData.serialNumber}
                    onChange={e => setFormData({...formData, serialNumber: e.target.value})}
                    placeholder="Manufacturer serial"
                  />
                  <span className="form-help">Manufacturer device serial number</span>
                </div>
              </div>
              <div className="form-group">
                <label>Widevine ID</label>
                <input
                  type="text"
                  value={formData.widevineId}
                  onChange={e => setFormData({...formData, widevineId: e.target.value})}
                  placeholder="Google Widevine DRM ID"
                />
                <span className="form-help">Required for secure DRM content playback</span>
              </div>
            </div>

            {/* Classification Section */}
            <div className="form-section">
              <h3>Classification</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Platform Type *</label>
                  <select
                    value={formData.platformType}
                    onChange={e => setFormData({...formData, platformType: e.target.value})}
                    required
                  >
                    {enums.ad_platform_type?.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Mobility *</label>
                  <select
                    value={formData.geoMobility}
                    onChange={e => setFormData({...formData, geoMobility: e.target.value})}
                    required
                  >
                    {enums.geo_mobility?.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City *</label>
                  <select
                    value={formData.city}
                    onChange={e => setFormData({...formData, city: e.target.value})}
                    required
                  >
                    {enums.city?.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Aspect Ratio *</label>
                  <select
                    value={formData.aspectRatio}
                    onChange={e => setFormData({...formData, aspectRatio: e.target.value})}
                    required
                  >
                    {enums.aspect_ratio?.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Screen Specifications Section */}
            <div className="form-section">
              <h3>Screen Specifications (optional)</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Width (cm)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.screenWidth}
                    onChange={e => setFormData({...formData, screenWidth: e.target.value})}
                    placeholder="e.g., 25.4"
                  />
                </div>
                <div className="form-group">
                  <label>Height (cm)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.screenHeight}
                    onChange={e => setFormData({...formData, screenHeight: e.target.value})}
                    placeholder="e.g., 14.3"
                  />
                </div>
                <div className="form-group">
                  <label>Min Bid (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.minBid}
                    onChange={e => setFormData({...formData, minBid: e.target.value})}
                    placeholder="0.45"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !formData.nodeName}>
              {loading ? 'Adding...' : 'Add to Inventory'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Device Modal Component
export const EditDeviceModal = ({ device, enums, onClose, onSuccess, onError }) => {
  const [formData, setFormData] = useState({
    nodeName: device?.nodeName || '',
    platformType: device?.platformType || 'in_cab',
    geoMobility: device?.geoMobility || 'mobile',
    city: device?.city || 'mumbai',
    aspectRatio: device?.aspectRatio || '16:9',
    screenWidth: device?.screenWidth?.toString() || '',
    screenHeight: device?.screenHeight?.toString() || '',
    minBid: device?.minBid?.toString() || '0.45',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nodeName.trim()) {
      onError('Device name is required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        nodeName: formData.nodeName.trim(),
        platformType: formData.platformType,
        geoMobility: formData.geoMobility,
        city: formData.city,
        aspectRatio: formData.aspectRatio,
        screenWidth: formData.screenWidth ? parseFloat(formData.screenWidth) : 0,
        screenHeight: formData.screenHeight ? parseFloat(formData.screenHeight) : 0,
        minBid: formData.minBid ? parseFloat(formData.minBid) : 0.45,
      };
      const response = await deviceService.update(device.id, payload);
      onSuccess(response.message || 'Device updated successfully');
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to update device');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>✏️ Edit Device</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Device ID (readonly) */}
            <div className="form-group">
              <label>Device ID</label>
              <input type="text" value={device?.id || ''} disabled className="input-disabled" />
              <span className="form-help">ID cannot be changed</span>
            </div>

            {/* Hardware IDs (readonly) */}
            <div className="form-section">
              <h3>Hardware Identifiers (read-only)</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>IMEI</label>
                  <input type="text" value={device?.imei || '—'} disabled className="input-disabled" />
                </div>
                <div className="form-group">
                  <label>Serial Number</label>
                  <input type="text" value={device?.serialNumber || '—'} disabled className="input-disabled" />
                </div>
                <div className="form-group">
                  <label>Widevine ID</label>
                  <input type="text" value={device?.widevineId || '—'} disabled className="input-disabled" />
                </div>
              </div>
            </div>

            {/* Device Name */}
            <div className="form-group">
              <label>Device Name *</label>
              <input
                type="text"
                value={formData.nodeName}
                onChange={e => setFormData({...formData, nodeName: e.target.value})}
                minLength={2}
                maxLength={100}
                required
              />
            </div>

            {/* Classification */}
            <div className="form-section">
              <h3>Classification</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Platform Type</label>
                  <select
                    value={formData.platformType}
                    onChange={e => setFormData({...formData, platformType: e.target.value})}
                  >
                    {enums.ad_platform_type?.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Mobility</label>
                  <select
                    value={formData.geoMobility}
                    onChange={e => setFormData({...formData, geoMobility: e.target.value})}
                  >
                    {enums.geo_mobility?.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <select
                    value={formData.city}
                    onChange={e => setFormData({...formData, city: e.target.value})}
                  >
                    {enums.city?.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Aspect Ratio</label>
                  <select
                    value={formData.aspectRatio}
                    onChange={e => setFormData({...formData, aspectRatio: e.target.value})}
                  >
                    {enums.aspect_ratio?.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Screen Specifications */}
            <div className="form-section">
              <h3>Screen Specifications</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Width (cm)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.screenWidth}
                    onChange={e => setFormData({...formData, screenWidth: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Height (cm)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.screenHeight}
                    onChange={e => setFormData({...formData, screenHeight: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Min Bid (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.minBid}
                    onChange={e => setFormData({...formData, minBid: e.target.value})}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Assign Device Modal Component
// Handles the V276 enrollment gate entirely in-modal so both call sites
// (Devices.js, DeviceDetail.js) stay untouched: a 409 with code
// PRODUCT_NOT_ENROLLED swaps the body to a confirm view, and confirming
// retries the SAME assign with { enroll_media_console: true } — one step for
// the operator, one audit trail for the tenant.
export const AssignDeviceModal = ({ device, tenants, onClose, onSuccess, onError }) => {
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [enrollPrompt, setEnrollPrompt] = useState(false);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const filteredTenants = tenants.filter(t =>
    t.isActive && t.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedTenantName = tenants.find(t => t.id === selectedTenantId)?.name;

  const doAssign = async (opts) => {
    setLoading(true);
    try {
      const response = await deviceService.assignTenant(device.id, selectedTenantId, opts);
      onSuccess(response.message || 'Device assigned to tenant');
    } catch (err) {
      // Error bodies are top-level { error, code } — read `code`, never render
      // an object as a string. The !opts guard keeps a PRODUCT_NOT_ENROLLED on
      // the retry itself from looping back into the prompt.
      if (!opts?.enroll_media_console && err.response?.status === 409 && err.response?.data?.code === 'PRODUCT_NOT_ENROLLED') {
        setEnrollPrompt(true);
      } else {
        onError(err.response?.data?.error || 'Failed to assign device');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedTenantId) {
      onError('Please select a tenant');
      return;
    }

    await doAssign(undefined);
  };

  if (enrollPrompt) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Enroll tenant in Media Console?</h2>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <p>
              <strong>{selectedTenantName || 'This workspace'}</strong> is not enrolled in
              Media Console. Enroll and assign?
            </p>
            <ul className="warning-list">
              <li>Enrolling turns on the Media Console product for this tenant</li>
              <li>The device will then be assigned in the same step</li>
            </ul>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setEnrollPrompt(false)}>
              Back
            </button>
            {/* Constructive confirm, so btn-primary — danger is reserved for
                destructive confirms like Unassign/Archive. */}
            <button
              type="button"
              className="btn btn-primary"
              disabled={loading}
              onClick={() => { setEnrollPrompt(false); doAssign({ enroll_media_console: true }); }}
            >
              {loading ? 'Enrolling…' : 'Enroll & Assign'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🔗 Assign Device to Tenant</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Device Info */}
            <div className="form-group">
              <label>Device</label>
              <div className="device-preview">
                <strong>{device?.nodeName}</strong>
                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                  {device?.imei && <span>IMEI: {device.imei}</span>}
                  {device?.serialNumber && <span> | SN: {device.serialNumber}</span>}
                </div>
              </div>
            </div>

            {/* Tenant Search */}
            <div className="form-group">
              <label>Search Tenants</label>
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="🔍 Search by name..."
              />
            </div>

            {/* Tenant Selection */}
            <div className="form-group">
              <label>Select Tenant *</label>
              <select
                value={selectedTenantId}
                onChange={e => setSelectedTenantId(e.target.value)}
                required
                size={5}
                style={{ height: 'auto' }}
              >
                <option value="">-- Select a tenant --</option>
                {filteredTenants.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name} {tenant.deviceCount > 0 ? `(${tenant.deviceCount} devices)` : ''}
                  </option>
                ))}
              </select>
              <span className="form-help">Device will be available for this tenant's ad campaigns.</span>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !selectedTenantId}>
              {loading ? 'Assigning...' : 'Assign Device'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Unassign Confirmation Modal
export const UnassignConfirmModal = ({ device, onClose, onConfirm, loading }) => {
  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>⚠️ Return Device to Inventory?</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p>Are you sure you want to unassign <strong>{device?.nodeName}</strong> from <strong>{device?.tenantName}</strong>?</p>
          <ul className="warning-list">
            <li>Device will be returned to inventory</li>
            <li>Device will no longer appear in tenant's device list</li>
            <li>You can reassign to another tenant later</li>
          </ul>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button 
            type="button" 
            className="btn btn-danger" 
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Return to Inventory'}
          </button>
        </div>
      </div>
    </div>
  );
};
